import { createSelector } from 'reselect';

const selectEdgeDevices = (state) => state.edgeDevices;

// ============================================================================
// CLUSTER SELECTORS
// ============================================================================

export const selectClusters = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.clusters || []
);

export const selectClusterById = (clusterId) => createSelector(
  [selectClusters],
  (clusters) => clusters.find(cluster => cluster._id === clusterId)
);

export const selectClusterDetails = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.clusterDetails || {}
);

export const selectClusterDetailsById = (clusterId) => createSelector(
  [selectClusterDetails],
  (clusterDetails) => clusterDetails[clusterId]
);

export const selectClusterHealth = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.clusterHealth || {}
);

export const selectClusterHealthById = (clusterId) => createSelector(
  [selectClusterHealth],
  (clusterHealth) => clusterHealth[clusterId]
);

export const selectServiceStatus = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.serviceStatus
);

export const selectActiveCluster = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.activeCluster
);

export const selectActiveNamespace = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.activeNamespace || 'default'
);

// Cluster statistics
export const selectClusterStats = (clusterId) => createSelector(
  [selectNodesByClusterId(clusterId), selectApplicationsByClusterId(clusterId)],
  (nodes, applications) => {
    const totalNodes = nodes.length;
    const readyNodes = nodes.filter(node => node.status === 'active' || node.status === 'Ready').length;
    const totalApps = applications.length;
    const runningApps = applications.filter(app => app.status === 'running').length;
    
    // Calculate total resources if available
    const totalCpu = nodes.reduce((sum, node) => {
      if (node.cpuUsage !== undefined) {
        return sum + (node.cpuUsage || 0);
      }
      return sum;
    }, 0);
    
    const totalMemory = nodes.reduce((sum, node) => {
      if (node.memoryUsage !== undefined) {
        return sum + (node.memoryUsage || 0);
      }
      return sum;
    }, 0);
    
    return {
      totalNodes,
      readyNodes,
      notReadyNodes: totalNodes - readyNodes,
      totalApps,
      runningApps,
      stoppedApps: totalApps - runningApps,
      avgCpuUsage: totalNodes > 0 ? totalCpu / totalNodes : 0,
      avgMemoryUsage: totalNodes > 0 ? totalMemory / totalNodes : 0
    };
  }
);

// ============================================================================
// NODE SELECTORS
// ============================================================================

export const selectAllNodes = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.nodes || {}
);

export const selectNodesByClusterId = (clusterId) => createSelector(
  [selectAllNodes],
  (nodes) => nodes[clusterId] || []
);

export const selectUnassignedNodes = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.unassignedNodes || []
);

export const selectNodeTelemetry = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.nodeTelemetry || {}
);

export const selectNodeTelemetryById = (nodeId) => createSelector(
  [selectNodeTelemetry],
  (telemetry) => telemetry[nodeId]
);

export const selectTotalNodesCount = createSelector(
  [selectAllNodes, selectUnassignedNodes],
  (nodes, unassignedNodes) => {
    const assignedNodesCount = Object.values(nodes).reduce(
      (total, clusterNodes) => total + (clusterNodes?.length || 0),
      0
    );
    return assignedNodesCount + (unassignedNodes?.length || 0);
  }
);

export const selectNodeById = (nodeId) => createSelector(
  [selectAllNodes, selectUnassignedNodes],
  (nodes, unassignedNodes) => {
    // Search in all cluster nodes
    for (const clusterNodes of Object.values(nodes)) {
      const node = clusterNodes.find(n => n._id === nodeId);
      if (node) return node;
    }
    // Search in unassigned nodes
    return unassignedNodes.find(n => n._id === nodeId);
  }
);

// ============================================================================
// APPLICATION SELECTORS
// ============================================================================

export const selectAllApplications = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.applications || {}
);

export const selectApplicationsByClusterId = (clusterId) => createSelector(
  [selectAllApplications],
  (applications) => applications[clusterId] || []
);

export const selectApplicationDetails = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.applicationDetails || {}
);

export const selectApplicationDetailsById = (appId) => createSelector(
  [selectApplicationDetails],
  (appDetails) => appDetails[appId]
);

export const selectApplicationById = (clusterId, appId) => createSelector(
  [selectApplicationsByClusterId(clusterId)],
  (applications) => applications.find(app => app._id === appId)
);

// ============================================================================
// SERVICES AND PODS SELECTORS
// ============================================================================

export const selectAllServices = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.services || {}
);

export const selectServicesByClusterId = (clusterId) => createSelector(
  [selectAllServices],
  (services) => services[clusterId] || []
);

export const selectAllPods = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.pods || {}
);

export const selectPodsByClusterId = (clusterId) => createSelector(
  [selectAllPods],
  (pods) => pods[clusterId] || []
);

// ============================================================================
// DEVICE REGISTRATION SELECTORS (Admin Only)
// ============================================================================

export const selectPendingDevices = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.pendingDevices || []
);

export const selectIsLoadingDevices = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isLoadingDevices
);

export const selectDeviceError = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.deviceError
);

export const selectPendingDeviceById = (deviceId) => createSelector(
  [selectPendingDevices],
  (devices) => devices.find(device => device._id === deviceId)
);

// ============================================================================
// LOADING STATE SELECTORS
// ============================================================================

export const selectIsFetching = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isFetching
);

export const selectIsCreating = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isCreating
);

export const selectIsDeleting = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isDeleting
);

export const selectIsUpdating = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isUpdating
);

export const selectIsInstalling = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isInstalling
);

export const selectIsExecutingCommand = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isExecutingCommand
);

// Combined loading state for UI
export const selectIsLoading = createSelector(
  [selectIsFetching, selectIsCreating, selectIsDeleting, selectIsUpdating, selectIsInstalling],
  (isFetching, isCreating, isDeleting, isUpdating, isInstalling) => 
    isFetching || isCreating || isDeleting || isUpdating || isInstalling
);

// ============================================================================
// TERMINAL SELECTORS
// ============================================================================

export const selectTerminalOutput = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.terminalOutput || []
);

export const selectIsTerminalConnected = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.terminalConnected
);

// ============================================================================
// ERROR SELECTORS
// ============================================================================

export const selectErrors = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.errors
);

export const selectHasErrors = createSelector(
  [selectErrors],
  (errors) => !!errors
);

// ============================================================================
// RBAC SELECTORS
// ============================================================================

export const selectRbacUsers = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.rbacUsers || []
);

export const selectRoles = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.roles || []
);

export const selectRoleByName = (roleName) => createSelector(
  [selectRoles],
  (roles) => roles.find(role => role.name === roleName)
);

export const selectIsLoadingRbac = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => !!edgeDevices?.isLoadingRbac
);

export const selectRbacError = createSelector(
  [selectEdgeDevices],
  (edgeDevices) => edgeDevices?.rbacError
);

export const selectUserById = (userId) => createSelector(
  [selectRbacUsers],
  (users) => users.find(user => user._id === userId || user.userId === userId)
);

export const selectUsersByRole = (roleName) => createSelector(
  [selectRbacUsers],
  (users) => users.filter(user => user.role === roleName)
);

// ============================================================================
// UTILITY SELECTORS
// ============================================================================

// Check if user has any data (for initial login scenarios)
export const selectHasAnyData = createSelector(
  [selectClusters, selectUnassignedNodes, selectPendingDevices],
  (clusters, unassignedNodes, pendingDevices) => 
    (clusters?.length > 0) || (unassignedNodes?.length > 0) || (pendingDevices?.length > 0)
);

// Check if initial data loading is complete (for spinner component)
export const selectIsDevicesLoaded = createSelector(
  [selectIsFetching, selectIsLoadingDevices, selectClusters, selectUnassignedNodes],
  (isFetching, isLoadingDevices, clusters, unassignedNodes) => {
    // If we're currently fetching, not loaded
    if (isFetching || isLoadingDevices) {
      return false;
    }
    
    // If we have data or arrays are initialized (even if empty), consider loaded
    // This handles new users with no clusters/nodes gracefully
    return (
      Array.isArray(clusters) && 
      Array.isArray(unassignedNodes)
    );
  }
);

// Check if user is completely new (no data at all)
export const selectIsNewUser = createSelector(
  [selectIsDevicesLoaded, selectHasAnyData, selectErrors, selectDeviceError],
  (isLoaded, hasData, errors, deviceError) => {
    // Only consider new user if loading is complete, no data, and no errors
    return isLoaded && !hasData && !errors && !deviceError;
  }
);

// Get overall system health
export const selectSystemHealth = createSelector(
  [selectClusters, selectServiceStatus, selectClusterHealth],
  (clusters, serviceStatus, clusterHealth) => {
    if (!clusters?.length) {
      return { status: 'no-clusters', message: 'No clusters configured' };
    }
    
    if (!serviceStatus) {
      return { status: 'unknown', message: 'Service status unavailable' };
    }
    
    const healthyClusters = clusters.filter(cluster => {
      const health = clusterHealth[cluster._id];
      return health?.kubernetes?.status === 'healthy';
    }).length;
    
    const healthPercentage = (healthyClusters / clusters.length) * 100;
    
    if (healthPercentage === 100) {
      return { status: 'healthy', message: 'All systems operational' };
    } else if (healthPercentage >= 50) {
      return { status: 'warning', message: 'Some systems experiencing issues' };
    } else {
      return { status: 'critical', message: 'Multiple systems down' };
    }
  }
);

// Get cluster summary for dashboard
export const selectClusterSummary = createSelector(
  [selectClusters, selectAllNodes, selectAllApplications],
  (clusters, allNodes, allApplications) => {
    const totalClusters = clusters?.length || 0;
    const activeClusters = clusters?.filter(c => c.status === 'active')?.length || 0;
    
    const totalNodes = Object.values(allNodes).reduce(
      (sum, nodes) => sum + (nodes?.length || 0), 0
    );
    
    const totalApps = Object.values(allApplications).reduce(
      (sum, apps) => sum + (apps?.length || 0), 0
    );
    
    return {
      totalClusters,
      activeClusters,
      inactiveClusters: totalClusters - activeClusters,
      totalNodes,
      totalApps
    };
  }
);

// Get resource utilization across all clusters
export const selectResourceUtilization = createSelector(
  [selectAllNodes],
  (allNodes) => {
    const allNodesList = Object.values(allNodes).flat();
    
    if (!allNodesList.length) {
      return { cpu: 0, memory: 0, disk: 0 };
    }
    
    const totalCpu = allNodesList.reduce((sum, node) => sum + (node.cpuUsage || 0), 0);
    const totalMemory = allNodesList.reduce((sum, node) => sum + (node.memoryUsage || 0), 0);
    const totalDisk = allNodesList.reduce((sum, node) => sum + (node.diskUsage || 0), 0);
    
    return {
      cpu: totalCpu / allNodesList.length,
      memory: totalMemory / allNodesList.length,
      disk: totalDisk / allNodesList.length
    };
  }
); 